import * as React from 'react'
import {styled, useTheme} from '@mui/material/styles'
import Box from '@mui/material/Box'
import Drawer from '@mui/material/Drawer'
import CssBaseline from '@mui/material/CssBaseline'
import MuiAppBar, {AppBarProps as MuiAppBarProps} from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import List from '@mui/material/List'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import {ExchangeOrderResultLink} from './tabs/ExchangeOrderResultLink'
import {TransferRequestAdaWithLink} from './tabs/TransferRequestAdaWithLink'
import {TransferRequestAda} from './tabs/TransferRequestAda'

export const App = () => {
  const theme = useTheme()
  const [open, setOpen] = React.useState(false)
  const [selected, setSelected] = React.useState('exchange-order-result-link')

  const handleDrawerOpen = () => {
    setOpen(true)
  }

  const handleDrawerClose = () => {
    setOpen(false)
  }

  const component = Components[selected]

  return (
    <Box sx={{display: 'flex'}}>
      <CssBaseline />

      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{mr: 2, ...(open && {display: 'none'})}}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap component="div">
            {Labels[selected]}
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          'width': drawerWidth,
          'flexShrink': 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </DrawerHeader>

        <Divider />

        <List>
          <ListItem key="exchange-order-result-link" disablePadding>
            <ListItemButton
              onClick={() => {
                handleDrawerClose()
                setSelected('exchange-order-result-link')
              }}
            >
              <ListItemText primary={Labels['exchange-order-result-link']} />
            </ListItemButton>
          </ListItem>

          <ListItem key="transfer-request-ada-with-link" disablePadding>
            <ListItemButton
              onClick={() => {
                handleDrawerClose()
                setSelected('transfer-request-ada-with-link')
              }}
            >
              <ListItemText
                primary={Labels['transfer-request-ada-with-link']}
              />
            </ListItemButton>
          </ListItem>

          <ListItem key="transfer-request-ada" disablePadding>
            <ListItemButton
              onClick={() => {
                handleDrawerClose()
                setSelected('transfer-request-ada')
              }}
            >
              <ListItemText primary={Labels['transfer-request-ada']} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      <Main open={open}>
        <DrawerHeader />

        {component}
      </Main>
    </Box>
  )
}

const drawerWidth = 240
const Main = styled('main', {shouldForwardProp: (prop) => prop !== 'open'})<{
  open?: boolean
}>(({theme, open}) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}))

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({theme, open}) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

const DrawerHeader = styled('div')(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}))

const Labels: Record<string, string> = {
  'exchange-order-result-link': 'Exchange Order Result Link',
  'transfer-request-ada-with-link': 'Transfer Request Ada With Link',
  'transfer-request-ada': 'Transfer Request Ada',
}

const Components: Record<string, React.ReactNode> = {
  'exchange-order-result-link': <ExchangeOrderResultLink />,
  'transfer-request-ada-with-link': <TransferRequestAdaWithLink />,
  'transfer-request-ada': <TransferRequestAda />,
}
